import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, Sparkles, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api, CATEGORIES, formatAmount, currencySymbol } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const SPLIT_TYPES = [
  { value: "equal", label: "Equal" },
  { value: "exact", label: "Exact" },
  { value: "percent", label: "%" },
  { value: "shares", label: "Shares" },
];

export default function NewBill() {
  const { id: groupId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("food");
  const [paidBy, setPaidBy] = useState("");
  const [splitType, setSplitType] = useState("equal");
  const [participants, setParticipants] = useState({}); // user_id -> boolean (equal only)
  const [amounts, setAmounts] = useState({}); // user_id -> number (exact)
  const [percents, setPercents] = useState({}); // user_id -> number (percent)
  const [shares, setShares] = useState({}); // user_id -> number (shares)
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/groups/${groupId}`);
      setGroup(data);
      setPaidBy(user.id);
      const pMap = {};
      const zMap = {};
      data.members.forEach((m) => { pMap[m.id] = true; zMap[m.id] = ""; });
      setParticipants(pMap);
      setAmounts(zMap); setPercents(zMap); setShares(zMap);
    })();
  }, [groupId, user.id]);

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        const base64 = String(dataUrl).split(",")[1];
        try {
          const { data } = await api.post("/ocr/receipt", { image_base64: base64 });
          if (data.title) setTitle(data.title);
          if (data.amount) setAmount(String(data.amount));
          if (data.category && CATEGORIES.find((c) => c.value === data.category)) {
            setCategory(data.category);
          }
          toast.success("Receipt scanned");
        } catch (err) {
          toast.error("OCR failed — enter manually");
        } finally {
          setOcrLoading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setOcrLoading(false);
    }
  };

  const activeMembers = () => (group?.members || []).filter((m) => participants[m.id]);

  const computeSplits = () => {
    const amt = Number(amount || 0);
    if (!amt || !group) return [];
    if (splitType === "equal") {
      const active = activeMembers();
      if (active.length === 0) return [];
      const share = amt / active.length;
      // distribute remainder to first member for cent-perfect splits
      const rounded = Math.round(share * 100) / 100;
      const total = rounded * active.length;
      const diff = Math.round((amt - total) * 100) / 100;
      return active.map((m, idx) => ({
        user_id: m.id,
        amount: Math.round((rounded + (idx === 0 ? diff : 0)) * 100) / 100,
      }));
    }
    if (splitType === "exact") {
      return group.members
        .map((m) => ({ user_id: m.id, amount: Number(amounts[m.id] || 0) }))
        .filter((s) => s.amount > 0);
    }
    if (splitType === "percent") {
      const total = group.members.reduce((s, m) => s + Number(percents[m.id] || 0), 0);
      if (total === 0) return [];
      return group.members
        .filter((m) => Number(percents[m.id] || 0) > 0)
        .map((m) => ({
          user_id: m.id,
          amount: Math.round((amt * (Number(percents[m.id]) / total)) * 100) / 100,
          meta: { percent: Number(percents[m.id]) },
        }));
    }
    if (splitType === "shares") {
      const total = group.members.reduce((s, m) => s + Number(shares[m.id] || 0), 0);
      if (total === 0) return [];
      return group.members
        .filter((m) => Number(shares[m.id] || 0) > 0)
        .map((m) => ({
          user_id: m.id,
          amount: Math.round((amt * (Number(shares[m.id]) / total)) * 100) / 100,
          meta: { shares: Number(shares[m.id]) },
        }));
    }
    return [];
  };

  const splits = computeSplits();
  const splitTotal = splits.reduce((s, x) => s + x.amount, 0);
  const splitDiff = Math.round((Number(amount || 0) - splitTotal) * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !amount || !paidBy || splits.length === 0) {
      toast.error("Fill title, amount, payer, and splits");
      return;
    }
    // For exact/percent/shares, adjust tiny rounding error by pushing diff to first split
    let finalSplits = splits;
    if (Math.abs(splitDiff) > 0 && Math.abs(splitDiff) < 0.05 && finalSplits.length > 0) {
      finalSplits = [...finalSplits];
      finalSplits[0] = { ...finalSplits[0], amount: Math.round((finalSplits[0].amount + splitDiff) * 100) / 100 };
    } else if (Math.abs(splitDiff) >= 0.05) {
      toast.error(`Splits must total ${formatAmount(amount, group.currency)} (off by ${splitDiff.toFixed(2)})`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/bills", {
        group_id: groupId,
        title, description: desc || null,
        amount: Number(amount),
        currency: group.currency,
        category, paid_by: paidBy,
        split_type: splitType,
        splits: finalSplits,
      });
      toast.success("Bill added");
      nav(`/groups/${groupId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!group) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-10 text-foreground/40">Loading…</div>
      </div>
    );
  }

  const sym = currencySymbol(group.currency);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <Link to={`/groups/${groupId}`} className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-white mb-6" data-testid="new-bill-back">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to {group.name}
        </Link>

        <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-2">/ New bill</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mb-8">Add an expense</h1>

        <form onSubmit={submit} className="space-y-6" data-testid="new-bill-form">
          {/* OCR zone */}
          <div className="rounded-xl border border-dashed border-border p-4 flex items-center justify-between gap-4" data-testid="ocr-zone">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-foreground/5 flex items-center justify-center">
                {ocrLoading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="h-4 w-4 brand-text" strokeWidth={1.5} />}
              </div>
              <div>
                <div className="text-sm">Scan a receipt</div>
                <div className="text-xs text-foreground/40">AI fills the form for you</div>
              </div>
            </div>
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={handleReceiptUpload} className="hidden" data-testid="ocr-file-input" />
              <span className="inline-flex items-center h-9 px-4 rounded-md border border-border bg-foreground/[0.03] hover:bg-foreground/[0.08] transition text-sm">
                <Upload className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Upload
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Title</Label>
              <Input required value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 bg-muted border-border h-11" placeholder="Dinner at Miss Margarita" data-testid="bill-title-input" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Amount</Label>
              <div className="mt-1.5 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-foreground/50">{sym}</span>
                <Input required type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-8 bg-muted border-border h-11 font-mono" placeholder="0.00" data-testid="bill-amount-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="bill-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Notes (optional)</Label>
              <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1.5 bg-muted border-border" rows={2} placeholder="Table of 4, tipped 18%…" data-testid="bill-notes-input" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Paid by</Label>
              <Select value={paidBy} onValueChange={setPaidBy}>
                <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="bill-paidby">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {group.members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.avatar_emoji || "🙂"} {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Split mode */}
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50 mb-2 block">Split mode</Label>
            <Tabs value={splitType} onValueChange={setSplitType}>
              <TabsList className="bg-background border border-border" data-testid="split-type-tabs">
                {SPLIT_TYPES.map((s) => (
                  <TabsTrigger key={s.value} value={s.value} className="data-[state=active]:brand-bg data-[state=active]:text-black" data-testid={`split-tab-${s.value}`}>
                    {s.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Split editor */}
          <div className="rounded-xl border border-border bg-card/40 p-4" data-testid="split-editor">
            <div className="space-y-2">
              {group.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-md px-2 py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    {splitType === "equal" && (
                      <input
                        type="checkbox"
                        checked={!!participants[m.id]}
                        onChange={(e) => setParticipants({ ...participants, [m.id]: e.target.checked })}
                        className="h-4 w-4 accent-[var(--brand)]"
                        data-testid={`split-equal-${m.id}`}
                      />
                    )}
                    <span className="h-8 w-8 rounded-full bg-foreground/10 flex items-center justify-center text-sm shrink-0">{m.avatar_emoji || "🙂"}</span>
                    <span className="text-sm truncate">{m.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {splitType === "exact" && (
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-foreground/50">{sym}</span>
                        <Input
                          type="number" step="0.01" value={amounts[m.id] ?? ""}
                          onChange={(e) => setAmounts({ ...amounts, [m.id]: e.target.value })}
                          className="w-28 h-8 text-sm pl-5 bg-muted border-border font-mono"
                          data-testid={`split-exact-${m.id}`}
                        />
                      </div>
                    )}
                    {splitType === "percent" && (
                      <div className="relative">
                        <Input
                          type="number" step="0.1" value={percents[m.id] ?? ""}
                          onChange={(e) => setPercents({ ...percents, [m.id]: e.target.value })}
                          className="w-24 h-8 text-sm pr-6 bg-muted border-border font-mono"
                          data-testid={`split-percent-${m.id}`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-foreground/50">%</span>
                      </div>
                    )}
                    {splitType === "shares" && (
                      <Input
                        type="number" step="1" value={shares[m.id] ?? ""}
                        onChange={(e) => setShares({ ...shares, [m.id]: e.target.value })}
                        className="w-20 h-8 text-sm bg-muted border-border font-mono"
                        placeholder="0"
                        data-testid={`split-shares-${m.id}`}
                      />
                    )}
                    <span className="text-xs font-mono text-foreground/60 w-20 text-right" data-testid={`split-computed-${m.id}`}>
                      {(() => {
                        const s = splits.find((x) => x.user_id === m.id);
                        return s ? formatAmount(s.amount, group.currency) : "—";
                      })()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs font-mono uppercase tracking-wider">
              <span className="text-foreground/40">Split total</span>
              <span className={Math.abs(splitDiff) < 0.05 ? "brand-text" : "text-red-400"} data-testid="split-total-display">
                {formatAmount(splitTotal, group.currency)} / {formatAmount(Number(amount || 0), group.currency)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Link to={`/groups/${groupId}`}>
              <Button type="button" variant="ghost" className="text-foreground/60">Cancel</Button>
            </Link>
            <Button type="submit" disabled={saving} className="brand-bg text-black hover:opacity-90 h-11 px-6" data-testid="bill-submit-btn">
              {saving ? "Saving…" : "Add bill"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
